import React, { Suspense, lazy } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import PrivateRoute from '../components/PrivateRoute';
import AppLayout from '../components/layout/AppLayout';
import { PageDependencyLoaderPreview } from '../components/common/PageDependencyLoader';
import { Spinner } from '../components/ui/Spinner/Spinner';
import { APP_ROUTES } from '../constants/routes';
import { isDistributorPath } from '../features/orders/utils/distributorContext';
import { orderRefIdFromSalesOrdersPath } from '../features/orders/utils/orderDetailPath';
import { stagedOrderRefIdFromPath } from '../features/stagedOrders/utils/stagedOrderDetailPath';

/**
 * Central route tree — single owner of in-app paths and their guards.
 *
 * Mirrors RW-react-core-standards `frontend-architecture.md` §5 and
 * `coding-standards.md` §12: routes module + `<RequireAuth>` wrapper +
 * a `*` catch-all. Path strings come from `APP_ROUTES` so callers never
 * hardcode URLs.
 *
 * Pages are lazy-loaded per chunk (§12) so heavy screens stay out of the
 * initial bundle; a single Suspense boundary renders the fallback while a
 * chunk loads (once per route per session — loaded chunks navigate instantly).
 */
const Login = lazy(() => import('../pages/Login'));
const SalesSummary = lazy(() => import('../pages/SalesSummary'));
const SalesBreakdown = lazy(() => import('../pages/SalesBreakdown'));
const DirectAuth = lazy(() => import('../pages/DirectAuth'));
const Dashboard = lazy(() => import('../pages/Dashboard'));
const ArContacts = lazy(() => import('../pages/ArContacts'));
const CeoPricing = lazy(() => import('../pages/CeoPricing'));
const CeoPricingDetail = lazy(() => import('../pages/CeoPricingDetail'));
const ShippingPrices = lazy(() => import('../pages/ShippingPrices'));
const Spi = lazy(() => import('../pages/Spi'));
const CloseHub = lazy(() => import('../pages/CloseHub'));
const AsanaDashboard = lazy(() => import('../pages/AsanaDashboard'));
const ApiKeys = lazy(() => import('../pages/ApiKeys'));
const ApiKeyForm = lazy(() => import('../pages/ApiKeyForm'));
const AmazonAds = lazy(() => import('../pages/AmazonAds'));
const Orders = lazy(() => import('../pages/Orders'));
const StagedOrders = lazy(() => import('../pages/StagedOrders'));
const StagedOrderDetail = lazy(() => import('../pages/StagedOrderDetail'));
const OrderDetail = lazy(() => import('../pages/OrderDetail'));
const ComingSoon = lazy(() => import('../pages/ComingSoon'));
const SearchResults = lazy(() => import('../pages/SearchResults'));

const RouteFallback: React.FC = () => (
  <div className="flex min-h-[50vh] items-center justify-center">
    <Spinner size="lg" />
  </div>
);

/**
 * Loader-first fallback for the AR Contacts route. Renders the SAME
 * loading view the page itself shows once it mounts — so while the lazy
 * chunk downloads the user sees the loader straight away instead of the
 * generic <Spinner>. When the chunk lands, the page's live
 * <PageDependencyLoader> takes over with identical chrome, so there's no
 * visual jump.
 */
const ArContactsRouteFallback: React.FC = () => <PageDependencyLoaderPreview />;

/**
 * Landing route (`/`) — sends the user to the orders listing, matching the Vue
 * ERP's own default screen (`ProfileLoader.vue` pushes `/orders/sales-orders/all`).
 *
 * A cross-origin document load rather than a `<Navigate>`, because that module
 * has no React route: it is not in `APP_ROUTES`, has no page component, and the
 * menu's own "All orders" entry carries `isReactRoute: false`. Hitting `/`
 * previously landed on the React dashboard, which is a component showcase — not
 * a screen a user should arrive at.
 *
 * `location.replace`, not `href`: `/` is a pass-through, so it must not become a
 * history entry the Back button returns to, which would bounce straight back out
 * again. Rendered inside the authenticated group on purpose — `PrivateRoute`
 * then handles a signed-out visitor (to React's own `/login`, carrying
 * `?redirect=`), so only a session-holding user is sent to the Vue origin.
 */
/**
 * Wildcard catch-all for `/orders/sales-orders/*` — routes a registered
 * distributor pathname (Amazon Canada, GFS US, US Foods CS, …) to the
 * migrated `<Orders />` page so it can apply the matching
 * `distributorId` / `distributorGroupId` filter; everything else falls
 * back to `<ComingSoon />`. Registry lives in
 * `features/orders/utils/distributorContext.ts` — adding a new
 * distributor is a single row there, no route registration needed.
 */
const DistributorOrComingSoon: React.FC = () => {
  const location = useLocation();
  if (isDistributorPath(location.pathname)) return <Orders />;
  // A UUID segment is one sales order (Vue `/orders/sales-orders/:orderId`).
  // Decided here by shape rather than a `:orderId` route, because a
  // dynamic segment would outrank the `/*` splat and capture every
  // distributor slug — see `orderRefIdFromSalesOrdersPath`.
  const refId = orderRefIdFromSalesOrdersPath(location.pathname);
  if (refId !== null) return <OrderDetail refId={refId} />;
  return <ComingSoon />;
};

/**
 * Catch-all for `/orders/staged-orders/*` below the static dealer scopes:
 * a UUID segment is one staged order (Vue `/orders/staged-orders/:orderId`),
 * anything else is not ported yet. Shape-decided here for the same reason
 * as `DistributorOrComingSoon` — a `:orderId` route would outrank the splat
 * and send every unrecognised segment to the detail page.
 */
const StagedOrderDetailOrComingSoon: React.FC = () => {
  const location = useLocation();
  const refId = stagedOrderRefIdFromPath(location.pathname);
  if (refId !== null) return <StagedOrderDetail refId={refId} />;
  return <ComingSoon />;
};

const AppRoutes: React.FC = () => {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Public */}
        <Route path={APP_ROUTES.login} element={<Login />} />
        <Route path={APP_ROUTES.authDirect} element={<DirectAuth />} />

        {/* Authenticated — share the layout */}
        <Route
          element={
            <PrivateRoute>
              <AppLayout />
            </PrivateRoute>
          }
        >
          <Route path={APP_ROUTES.dashboard} element={<Dashboard />} />
          <Route path={APP_ROUTES.ceoPricing} element={<CeoPricing />} />
          <Route path={APP_ROUTES.ceoPricingDetail} element={<CeoPricingDetail />} />
          {/* The listing under the same prefixes. `/products/ceo-pricing` is
              the ERP's own path for this screen, so it must reach the listing
              rather than be read as a SKU by the alias below. */}
          {APP_ROUTES.ceoPricingListingAliases.map((path) => (
            <Route key={path} path={path} element={<CeoPricing />} />
          ))}
          {/* The detail page under the paths people actually have in hand —
              `/products/...`, `/products/pricing/...`, and both without the
              trailing `s`. Each resolves the param and then REPLACES the URL
              with the canonical `/ceo-pricing/<productId>`, so nothing
              downstream needs to know these exist. */}
          {APP_ROUTES.ceoPricingDetailAliases.map((path) => (
            <Route key={path} path={path} element={<CeoPricingDetail />} />
          ))}
          <Route path={APP_ROUTES.salesSummary} element={<SalesSummary />} />
          <Route path={APP_ROUTES.salesBreakdown} element={<SalesBreakdown />} />
          <Route
            path={APP_ROUTES.arContacts}
            element={
              // Dedicated Suspense boundary so the AR Contacts chunk-load
              // shows the dependency timeline (timeline-first boot) instead
              // of the generic spinner used by the outer boundary.
              <Suspense fallback={<ArContactsRouteFallback />}>
                <ArContacts />
              </Suspense>
            }
          />
          <Route path={APP_ROUTES.shippingPrices} element={<ShippingPrices />} />
          {/*
            Splat path keeps the single <Spi /> element mounted across the
            inventory / buyers / settings sub-views so in-memory SPI state
            (assumptions, OOS factor, PO drafts, filters) survives view
            switches while each view still has its own URL.
          */}
          <Route path={`${APP_ROUTES.spiInventory}/*`} element={<Spi />} />
          {/*
            CloseHub (month-end close). Splat keeps the single <CloseHub />
            element mounted across the dashboard / checklist / recon views, so
            filters, collapse state and a running QB or Excel sync survive a
            view switch — the standalone app relied on that too.
          */}
          <Route path={`${APP_ROUTES.closeHub}/*`} element={<CloseHub />} />
          {/* Asana Sourcing Dashboard — splat keeps the component mounted across team sub-routes. */}
          <Route path={`${APP_ROUTES.asanaDashboard}/*`} element={<AsanaDashboard />} />
          <Route path={APP_ROUTES.asanaDashboard} element={<Navigate to={APP_ROUTES.asanaSourcing} replace />} />
          {/*
            API Key Management Portal (React-only).
            Three URL-addressable views:
              /settings/api-keys             -> redirect to /list
              /settings/api-keys/list        -> key listing + RevealSecretModal
              /settings/api-keys/NEW_API_KEY -> create form
              /settings/api-keys/:apiKeyId   -> edit / view form
          */}
          <Route
            path={APP_ROUTES.apiKeys}
            element={<Navigate to={APP_ROUTES.apiKeysList} replace />}
          />
          <Route path={APP_ROUTES.apiKeysList} element={<ApiKeys />} />
          <Route path="/settings/api-keys/:apiKeyId" element={<ApiKeyForm />} />

          {/* Marketing — Ads & Sales Reconciliation dashboard (React-only). */}
          <Route path={APP_ROUTES.amazonAds} element={<AmazonAds />} />
          {/* Orders — migrated React listing. */}
          <Route path={APP_ROUTES.orders} element={<Orders />} />
          {/* Sales orders > All orders — render the migrated React
              <Orders /> page at the Vue-style URL. MUST be registered
              ABOVE the `/orders/sales-orders/*` wildcard below so React
              Router prefers the exact match. */}
          <Route path={APP_ROUTES.ordersSalesOrdersAll} element={<Orders />} />
          {/* Distributor-scoped listings (Amazon Canada, GFS US, US Foods
              CS, …) — `DistributorOrComingSoon` consults the registry in
              `features/orders/utils/distributorContext.ts` and renders
              `<Orders />` for registered paths (applying the matching
              `distributorId` / `distributorGroupId` filter), or
              `<ComingSoon />` for paths that aren't ported yet.
              Adding a new distributor is one row in the registry —
              no edits here. */}
          <Route
            path={`${APP_ROUTES.ordersSalesOrdersBase}/*`}
            element={<DistributorOrComingSoon />}
          />
          {/* Staged (dealer) orders — All + Jet-Fresh share one page;
              the Jet-Fresh route adds a dealerRefId scope (Vue parity:
              both render JetFreshView.vue). Exact matches sit ABOVE the
              wildcard so React Router prefers them. */}
          <Route path={APP_ROUTES.stagedOrdersAll} element={<StagedOrders />} />
          <Route
            path={APP_ROUTES.stagedOrdersJetfresh}
            element={<StagedOrders />}
          />
          {/* CW-Order — React-side dealer-scoped variant (no Vue route
              exists); scope comes from `STAGED_DEALER_SCOPES`. */}
          <Route
            path={APP_ROUTES.stagedOrdersCwOrder}
            element={<StagedOrders />}
          />
          {/* Everything else under staged orders: a UUID segment is one
              staged order's detail page, the rest is not ported yet. */}
          <Route
            path={`${APP_ROUTES.stagedOrdersBase}/*`}
            element={<StagedOrderDetailOrComingSoon />}
          />
          {/* Global-search results (Vue parity: `/search?search=&active=`),
              reached from the header search's Enter key. */}
          <Route path={APP_ROUTES.search} element={<SearchResults />} />

          {/* Landing page — inside the auth gate so a signed-out visitor goes to
              React's /login instead of being thrown at the Vue origin. Lands on
              the React Orders listing at the FULL path (Vue parity:
              `ProfileLoader.vue` pushes `/orders/sales-orders/all` — not the
              shorter `/orders` alias this used to redirect to, which left the
              aside menu's active-leaf detection without a genuine URL match
              since the "All orders" leaf's own `route` and `ordersRouteId`'s
              urlPathConfiguration pattern are both the full path). Keep last
              so the concrete routes above are matched first. */}
          <Route
            path={APP_ROUTES.root}
            element={<Navigate to={APP_ROUTES.ordersSalesOrdersAll} replace />}
          />
        </Route>
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
